import React, { memo } from "react";
import { Commercial, SelectedMenu, Voucher } from "../../../types";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { SummaryCommercialItem } from "./summary-commercial-item";
import { VoucherItem } from "../../voucher/voucher-item";
import { pendingBookingStore } from "../../../stores/pending-booking.store";
import { commercialsStore } from "../../../stores/commercials.store";

const SummaryWrapper = styled.div`
  padding: 10px;
  margin: 0 auto;
  width: 300px;
  border-radius: 10px;
  border: 1px solid ${(props) => props.theme.disabledColor};
  background-color: ${(props) => props.theme.backgroundColor};
  margin-bottom: 24px;
`;

const Subtitle = styled.div`
  color: ${(props) => props.theme.disabledColor};
  font-size: 12px;
`;

const Separator = styled.hr`
  color: ${(props) => props.theme.disabledColor};
  opacity: 0.4;
  width: 100%;
  padding: 0 4px;
  margin: 0;
`;

const CommercialsTotal = styled.div`
  width: 100%;
  text-align: right;
  font-size: 14px;
  font-weight: bold;
`;

const CommercialsSummary = () => {
  const { t } = useTranslation();
  const {
    persons,
    event,
    offer,
    selected_menus: menus,
    prepayment,
    vouchers,
  } = pendingBookingStore.pendingBooking;
  const _event =
    event &&
    commercialsStore.events?.filter(
      (commercial) => commercial.id === event.commercial_id
    )[0];
  const _offer =
    offer &&
    commercialsStore.offers?.filter(
      (commercial) => commercial.id === offer.commercial_id
    )[0];

  const hasVoucher = !!vouchers?.length;
  const hasEvent = !!event;
  const hasOffer = !!offer;
  const hasMenus = !!menus?.length;

  const quantity = (commercial: any) => {
    if (prepayment?.origin === commercial.object && commercial.has_prepayment) {
      if (prepayment.kind === "seats") {
        return persons;
      }
    }
    return 0;
  };

  const unitPrice = (commercial: any) => {
    if (prepayment?.origin === commercial.object && commercial.has_prepayment) {
      return commercial.prepayment.price * 100;
    }
    return 0;
  };

  const currency = (commercial: any) => {
    if (prepayment?.origin === commercial.object && commercial.has_prepayment) {
      return commercial.prepayment.currency;
    }
    return 0;
  };

  const displaySelector = (commercialKind: string) => {
    return (
      <>
        <Subtitle data-test="subtitle-selector">{commercialKind}</Subtitle>
        <Separator />
      </>
    );
  };

  const selectedEvent = hasEvent ? (
    <SummaryCommercialItem
      key={_event.id}
      commercialId={_event.id.toString()}
      name={_event.name}
      quantity={quantity(_event)}
      unitPrice={unitPrice(_event)}
      currency={currency(_event)}
      image={_event.image}
    />
  ) : (
    <></>
  );

  const selectedOffer = hasOffer ? (
    <SummaryCommercialItem
      key={_offer.id}
      commercialId={_offer.id}
      name={_offer.name}
      quantity={quantity(_offer)}
      unitPrice={unitPrice(_offer)}
      currency={currency(_offer)}
      image={_offer.image}
    />
  ) : (
    <></>
  );

  const selectedMenus = hasMenus
    ? menus.map((menu: SelectedMenu) => (
        <SummaryCommercialItem
          key={menu.commercial_id}
          commercialId={menu.commercial_id}
          name={menu.name}
          quantity={menu.quantity}
          unitPrice={menu.unit_price}
          currency={menu.currency}
          image={menu.image}
        />
      ))
    : [];

  const addedVouchers = hasVoucher
    ? vouchers.map((voucher: Voucher) => (
        <VoucherItem voucher={voucher} key={voucher.code} />
      ))
    : [];

  const needToShowSummary = hasVoucher || hasEvent || hasOffer || hasMenus;

  return needToShowSummary ? (
    <SummaryWrapper data-test="summary">
      {hasVoucher && displaySelector(t("instabook.common.voucher"))}
      {hasVoucher && addedVouchers}

      {hasEvent && displaySelector(t("instabook.common.event"))}
      {hasEvent ? <>{selectedEvent}</> : ""}

      {hasOffer && displaySelector(t("instabook.common.offer"))}
      {hasOffer ? <>{selectedOffer}</> : ""}

      {hasMenus && displaySelector(t("instabook.common.menus"))}
      {hasMenus ? <>{selectedMenus}</> : ""}

      {prepayment && displaySelector(t("instabook.prepayment.total"))}
      {prepayment && (
        <CommercialsTotal data-test="commercials-total">
          <span>
            {pendingBookingStore.getPrepaymentPriceWithPrecision()?.toFixed(2)}{" "}
            {prepayment.currency}
          </span>
        </CommercialsTotal>
      )}
    </SummaryWrapper>
  ) : (
    <></>
  );
};

export const Summary = memo(CommercialsSummary);
